import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Loader2, Upload, X, ArrowLeft, Save, CreditCard, Info } from 'lucide-react';
import { TranslatedFileInput } from '@/components/ui/translated-file-input';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { registerProvider, uploadProviderPhoto, uploadProviderCredential, uploadProviderIdDocument } from '@/services/providerService';
import { useAuth } from '@/contexts/AuthContext';
import { useGeolocation } from '@/hooks/useGeolocation';
import { useLocationValidation } from '@/hooks/useLocationValidation';
import { supabase } from '@/integrations/supabase/client';
import { LocationAutocomplete } from '@/components/LocationAutocomplete';
import ProviderPaymentStep from './ProviderPaymentStep';
import ScheduleInput from './ScheduleInput';
import { useProviderDraft } from '@/hooks/useProviderDraft';

// Provider types that require professional credentials
const MEDICAL_PROVIDER_TYPES = ['veterinarian_individual', 'veterinary_clinic'] as const;

const formSchema = z.object({
  provider_type: z.enum(['veterinarian_individual', 'veterinary_clinic', 'pet_store', 'dog_walker', 'grooming_spa', 'daycare_hotel', 'school_training']),
  business_name: z.string().min(3, 'Business name must be at least 3 characters'),
  phone: z.string().min(10, 'Valid phone number required'),
  email: z.string().email('Valid email required'),
  whatsapp: z.string().optional(),
  description: z.string().min(20, 'Description must be at least 20 characters'),
  address: z.string().min(5, 'Complete address required'),
  city: z.string().optional(),
  state: z.string().optional(),
  country: z.string().optional(),
  registration_number: z.string().optional(),
  id_type: z.enum(['national_id', 'passport', 'drivers_license', 'residence_permit', 'tax_id']).optional(),
  id_number: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

export default function ProviderRegistrationForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { detectLocation, isDetecting } = useGeolocation();
  const { saveDraft, loadDraft, isSaving: isSavingDraft } = useProviderDraft();

  const [currentStep, setCurrentStep] = useState<'form' | 'payment'>('form');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [photos, setPhotos] = useState<File[]>([]);
  const [uploadedPhotos, setUploadedPhotos] = useState<string[]>([]);
  const [credential, setCredential] = useState<File | null>(null);
  const [uploadedCredential, setUploadedCredential] = useState<string>('');
  const [idDocumentFront, setIdDocumentFront] = useState<File | null>(null);
  const [idDocumentBack, setIdDocumentBack] = useState<File | null>(null);
  const [uploadedIdFrontUrl, setUploadedIdFrontUrl] = useState<string>('');
  const [uploadedIdBackUrl, setUploadedIdBackUrl] = useState<string>('');
  const [services, setServices] = useState<string[]>([]);
  const [newService, setNewService] = useState('');
  const [languages, setLanguages] = useState<string[]>([]);
  const [newLanguage, setNewLanguage] = useState('');
  const [hasDraftLoaded, setHasDraftLoaded] = useState(false);
  const [schedule, setSchedule] = useState({
    monday: { open: '08:00', close: '18:00', closed: false },
    tuesday: { open: '08:00', close: '18:00', closed: false },
    wednesday: { open: '08:00', close: '18:00', closed: false },
    thursday: { open: '08:00', close: '18:00', closed: false },
    friday: { open: '08:00', close: '18:00', closed: false },
    saturday: { open: '09:00', close: '14:00', closed: false },
    sunday: { open: '', close: '', closed: true },
    emergency_available: false,
  });
  const [coordinates, setCoordinates] = useState<{ lat: number; lon: number; address: string } | null>(null);
  const { validateLocation, geocodeAddress } = useLocationValidation();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  // Watch id_type to reset idDocumentBack when changing to passport
  const watchedIdType = form.watch('id_type');
  
  useEffect(() => {
    if (watchedIdType === 'passport') {
      setIdDocumentBack(null);
    }
  }, [watchedIdType]);

  // Load draft on mount
  useEffect(() => {
    const loadExistingDraft = async () => {
      const providerType = form.watch('provider_type');
      if (!providerType || hasDraftLoaded) return;

      const result = await loadDraft(providerType);
      if (result.success && result.draft) {
        const draft = result.draft;
        
        // Restore form fields
        Object.keys(draft).forEach((key) => {
          if (key in formSchema.shape && draft[key]) {
            form.setValue(key as any, draft[key]);
          }
        });

        // Restore coordinates
        if (draft.latitude && draft.longitude) {
          setCoordinates({ 
            lat: draft.latitude, 
            lon: draft.longitude,
            address: draft.address || '' 
          });
        }

        // Restore other states
        setServices(Array.isArray(draft.services) ? draft.services : []);
        setLanguages(Array.isArray(draft.languages) ? draft.languages : []);
        if (draft.schedule) setSchedule(draft.schedule);
        if (Array.isArray(draft.photos)) {
          setUploadedPhotos(draft.photos);
        }
        if (draft.credential_document_url) {
          setUploadedCredential(draft.credential_document_url);
        }
        if (draft.id_document_front_url) {
          setUploadedIdFrontUrl(draft.id_document_front_url);
        }
        if (draft.id_document_back_url) {
          setUploadedIdBackUrl(draft.id_document_back_url);
        }

        setHasDraftLoaded(true);
        toast.info(t('services.registerProvider.draftLoaded'));
      }
    };

    loadExistingDraft();
  }, [form.watch('provider_type')]);

  const extractAddressComponents = (formattedAddress: string) => {
    const parts = formattedAddress.split(', ');
    if (parts.length >= 2) {
      const country = parts[parts.length - 1];
      const state = parts.length >= 3 ? parts[parts.length - 2] : '';
      const city = parts.length >= 3 ? parts[parts.length - 3] : parts[parts.length - 2];
      
      form.setValue('city', city);
      form.setValue('state', state);
      form.setValue('country', country);
    }
  };

  const handleSaveDraft = async () => {
    try {
      console.log('🔵 [SAVE DRAFT] Starting save draft process');
      
      // Verificar autenticación
      if (!user || !user.id) {
        toast.error('User not authenticated. Please log in again.');
        console.error('❌ [SAVE DRAFT] User not authenticated:', user);
        navigate('/login');
        return;
      }

      console.log('✅ [SAVE DRAFT] User authenticated:', user.id);

      // Verificar sesión de Supabase
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Session expired. Please log in again.');
        console.error('❌ [SAVE DRAFT] No active session');
        navigate('/login');
        return;
      }

      console.log('✅ [SAVE DRAFT] Active session found');

      const formData = form.getValues();
      
      // Basic validation
      if (!formData.provider_type || !formData.business_name || !formData.phone || !formData.email) {
        toast.error(t('services.registerProvider.fillRequiredFields'));
        return;
      }

      if (!coordinates) {
        toast.error(t('services.registerProvider.locationRequired'));
        return;
      }

      // Upload files if not already uploaded
      let photoUrls = [...uploadedPhotos];
      let credentialUrl = uploadedCredential;

      if (photos.length > 0 && photoUrls.length === 0) {
        console.log('🔵 [SAVE DRAFT] Uploading photos:', photos.length);
        toast.info(t('services.registerProvider.uploadingFiles'));
        
        for (const photo of photos) {
          console.log('🔵 [SAVE DRAFT] Uploading photo:', photo.name);
          const url = await uploadProviderPhoto(photo, user.id);
          photoUrls.push(url);
          console.log('✅ [SAVE DRAFT] Photo uploaded:', url);
        }
        setUploadedPhotos(photoUrls);
        setPhotos([]); // Clear files after upload
      }

      if (credential && !credentialUrl) {
        console.log('🔵 [SAVE DRAFT] Uploading credential:', credential.name);
        credentialUrl = await uploadProviderCredential(credential, user.id);
        setUploadedCredential(credentialUrl);
        setCredential(null); // Clear file after upload
        console.log('✅ [SAVE DRAFT] Credential uploaded:', credentialUrl);
      }

      // Upload ID documents if not already uploaded
      let idFrontUrl = uploadedIdFrontUrl;
      let idBackUrl = uploadedIdBackUrl;

      if (idDocumentFront && !idFrontUrl) {
        console.log('🔵 [SAVE DRAFT] Uploading ID front:', idDocumentFront.name);
        idFrontUrl = await uploadProviderIdDocument(idDocumentFront, user.id, 'front');
        setUploadedIdFrontUrl(idFrontUrl);
        setIdDocumentFront(null);
        console.log('✅ [SAVE DRAFT] ID front uploaded:', idFrontUrl);
      }

      if (idDocumentBack && !idBackUrl) {
        console.log('🔵 [SAVE DRAFT] Uploading ID back:', idDocumentBack.name);
        idBackUrl = await uploadProviderIdDocument(idDocumentBack, user.id, 'back');
        setUploadedIdBackUrl(idBackUrl);
        setIdDocumentBack(null);
        console.log('✅ [SAVE DRAFT] ID back uploaded:', idBackUrl);
      }

      const draftData = {
        provider_type: formData.provider_type,
        business_name: formData.business_name,
        phone: formData.phone,
        email: formData.email,
        whatsapp: formData.whatsapp || '',
        description: formData.description || '',
        photos: photoUrls,
        services,
        languages,
        latitude: coordinates.lat,
        longitude: coordinates.lon,
        address: formData.address,
        city: formData.city || '',
        state: formData.state || '',
        country: formData.country || '',
        schedule,
        credential_document_url: credentialUrl,
        registration_number: formData.registration_number || '',
        id_document_front_url: idFrontUrl,
        id_document_back_url: idBackUrl,
      };

      console.log('🔵 [SAVE DRAFT] Saving draft data to Supabase');
      await saveDraft(draftData);
      console.log('✅ [SAVE DRAFT] Draft saved successfully');
      setHasDraftLoaded(true);
    } catch (error: any) {
      console.error('❌ [SAVE DRAFT] Error completo:', {
        message: error?.message,
        stack: error?.stack,
        error: error
      });
      
      // Show specific error message
      const errorMessage = error?.message || t('services.registerProvider.errorUploadingFiles');
      toast.error(`Error: ${errorMessage}`);
    }
  };

  const handleDetectLocation = async () => {
    const result = await detectLocation();
    if (result) {
      setCoordinates({ 
        lat: result.latitude, 
        lon: result.longitude,
        address: result.location
      });
      
      form.setValue('address', result.location);
      extractAddressComponents(result.location);
      
      toast.success(t('location.detected'));
    }
  };

  const handleAddService = () => {
    if (!newService.trim()) return;
    
    // Dividir por comas en caso de que haya múltiples servicios
    const servicesToAdd = newService.split(',')
      .map(service => service.trim())
      .filter(service => service && !services.includes(service));
    
    if (servicesToAdd.length > 0) {
      setServices([...services, ...servicesToAdd]);
      setNewService('');
    }
  };

  const handleRemoveService = (service: string) => {
    setServices(services.filter(s => s !== service));
  };

  const handleAddLanguage = () => {
    if (!newLanguage.trim()) return;
    
    // Dividir por comas en caso de que haya múltiples idiomas
    const languagesToAdd = newLanguage.split(',')
      .map(lang => lang.trim())
      .filter(lang => lang && !languages.includes(lang));
    
    if (languagesToAdd.length > 0) {
      setLanguages([...languages, ...languagesToAdd]);
      setNewLanguage('');
    }
  };

  const handleRemoveLanguage = (language: string) => {
    setLanguages(languages.filter(l => l !== language));
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      if (photos.length + newFiles.length > 5) {
        toast.error(t('services.registerProvider.maxPhotos'));
        return;
      }
      setPhotos([...photos, ...newFiles]);
    }
  };

  const onSubmit = async (data: FormData) => {
    // Validate coordinates are precise
    if (!coordinates || !coordinates.lat || !coordinates.lon) {
      toast.error(t('services.registerProvider.selectAddressFromSuggestions'));
      // Scroll to address field
      document.getElementById('location')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // Validate address is not empty or malformed
    if (!coordinates.address || coordinates.address.trim().length < 10) {
      toast.error(t('services.registerProvider.selectAddressFromSuggestions'));
      document.getElementById('location')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // Validate city before submission
    if (data.city) {
      const isCityValid = await validateLocation(data.city);
      if (!isCityValid) {
        toast.error(t('services.registerProvider.invalidCity'));
        return;
      }
    }

    // Validate address is not just the city name
    if (data.address && data.city && data.address.toLowerCase().trim() === data.city.toLowerCase().trim()) {
      toast.error(t('services.registerProvider.completeAddressRequired'));
      document.getElementById('location')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // Validate photos exist (either as files or already uploaded URLs)
    if (photos.length === 0 && uploadedPhotos.length === 0) {
      toast.error(t('services.registerProvider.photoRequired'));
      return;
    }

    // Validate credentials only for medical providers
    const providerType = form.getValues('provider_type');
    const isMedicalProvider = MEDICAL_PROVIDER_TYPES.includes(providerType as typeof MEDICAL_PROVIDER_TYPES[number]);

    if (isMedicalProvider) {
      // Validate credential exists (either as file or already uploaded URL)
      if (!credential && !uploadedCredential) {
        toast.error(t('services.registerProvider.credentialRequired'));
        return;
      }

      if (!idDocumentFront && !uploadedIdFrontUrl) {
        toast.error(t('services.registerProvider.idDocumentRequired'));
        return;
      }

      // Only validate idDocumentBack if NOT passport
      const selectedIdType = form.getValues('id_type');
      if (selectedIdType !== 'passport' && !idDocumentBack && !uploadedIdBackUrl) {
        toast.error(t('services.registerProvider.idDocumentBackRequired'));
        return;
      }

      // Validate registration number for medical providers
      const registrationNumber = form.getValues('registration_number');
      if (!registrationNumber || registrationNumber.length < 3) {
        toast.error(t('services.registerProvider.registrationNumberRequired'));
        return;
      }

      // Validate id_type and id_number for medical providers
      const idType = form.getValues('id_type');
      const idNumber = form.getValues('id_number');
      if (!idType) {
        toast.error(t('services.registerProvider.idTypeRequired'));
        return;
      }
      if (!idNumber || idNumber.length < 5) {
        toast.error(t('services.registerProvider.idNumberRequiredError'));
        return;
      }
    }

    setIsSubmitting(true);

    try {
      // Prepare data for temporary storage (only small data)
      const providerData = {
        formData: data,
        coordinates: coordinates,
        services,
        languages,
        schedule,
      };

      // Save only small form data to sessionStorage
      sessionStorage.setItem('pendingProviderRegistration', JSON.stringify(providerData));

      console.log('Provider data saved, proceeding to payment');
      toast.success(t('services.registerProvider.dataSaved'));
      
      // Move to payment step
      setCurrentStep('payment');
    } catch (error: any) {
      console.error('Error preparing data:', error);
      toast.error(error.message || 'Failed to prepare data');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (currentStep === 'payment') {
    return (
      <div className="space-y-4">
        <Button 
          variant="ghost" 
          onClick={() => setCurrentStep('form')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common.back')}
        </Button>
        <ProviderPaymentStep 
          photos={photos}
          credential={credential}
          idDocumentFront={idDocumentFront}
          idDocumentBack={idDocumentBack}
          uploadedPhotoUrls={uploadedPhotos}
          uploadedCredentialUrl={uploadedCredential}
          uploadedIdFrontUrl={uploadedIdFrontUrl}
          uploadedIdBackUrl={uploadedIdBackUrl}
        />
      </div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {/* Draft Indicator */}
        {hasDraftLoaded && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <p className="text-sm text-blue-800 dark:text-blue-200 flex items-center">
              <Info className="w-4 h-4 mr-2" />
              {t('services.registerProvider.draftExists')}
            </p>
          </div>
        )}
        {/* Provider Type */}
        <FormField
          control={form.control}
          name="provider_type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('services.registerProvider.providerType')} *</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={t('services.registerProvider.selectProviderType')} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                <SelectItem value="veterinarian_individual">
                  {t('services.providerTypes.veterinarian_individual')}
                </SelectItem>
                <SelectItem value="veterinary_clinic">
                  {t('services.providerTypes.veterinary_clinic')}
                </SelectItem>
                <SelectItem value="pet_store">
                  {t('services.providerTypes.pet_store')}
                </SelectItem>
                <SelectItem value="dog_walker">
                  {t('services.providerTypes.dog_walker')}
                </SelectItem>
                <SelectItem value="grooming_spa">
                  {t('services.providerTypes.grooming_spa')}
                </SelectItem>
                <SelectItem value="daycare_hotel">
                  {t('services.providerTypes.daycare_hotel')}
                </SelectItem>
                <SelectItem value="school_training">
                  {t('services.providerTypes.school_training')}
                </SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Business Name */}
        <FormField
          control={form.control}
          name="business_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('services.registerProvider.businessName')} *</FormLabel>
              <FormControl>
                <Input placeholder={t('services.registerProvider.businessNamePlaceholder')} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Contact Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('services.registerProvider.phone')} *</FormLabel>
                <FormControl>
                  <Input placeholder="+1234567890" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('services.registerProvider.email')} *</FormLabel>
                <FormControl>
                  <Input type="email" placeholder={t('services.registerProvider.emailPlaceholder')} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="whatsapp"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('services.registerProvider.whatsapp')}</FormLabel>
                <FormControl>
                  <Input placeholder="+1234567890" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Description */}
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('services.registerProvider.description')} *</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder={t('services.registerProvider.descriptionPlaceholder')} 
                  rows={4}
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Location */}
        <Card className="p-4 bg-muted/50">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">{t('services.registerProvider.location')}</h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleDetectLocation}
                disabled={isDetecting}
              >
                {isDetecting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                {coordinates ? t('services.registerProvider.updateLocation') : t('services.registerProvider.detectLocation')}
              </Button>
            </div>
            {coordinates && (
              <p className="text-sm text-green-600 flex items-center gap-2">
                <span>✓ {t('services.registerProvider.locationDetectedAddress')}: {coordinates.address}</span>
              </p>
            )}
            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('services.registerProvider.address')} *</FormLabel>
                  <FormControl>
                    <LocationAutocomplete
                      value={field.value || ''}
                      onChange={async (value, placeId) => {
                        field.onChange(value);
                        
                        if (placeId && geocodeAddress) {
                          toast.loading(t('services.registerProvider.geocodingAddress'));
                          const geocoded = await geocodeAddress(placeId);
                          toast.dismiss();
                          
                          if (geocoded) {
                            setCoordinates({
                              lat: geocoded.latitude,
                              lon: geocoded.longitude,
                              address: geocoded.formatted_address
                            });
                            
                            extractAddressComponents(geocoded.formatted_address);
                            toast.success(t('services.registerProvider.addressValidated'));
                          }
                        }
                      }}
                      placeholder={t('services.registerProvider.addressPlaceholder')}
                      type="address"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('services.registerProvider.city')}</FormLabel>
                    <FormControl>
                      <LocationAutocomplete
                        value={field.value || ''}
                        onChange={field.onChange}
                        placeholder={t('services.registerProvider.selectCityFromSuggestions')}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="state"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('services.registerProvider.state')}</FormLabel>
                    <FormControl>
                      <Input placeholder={t('services.registerProvider.statePlaceholder')} {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('services.registerProvider.country')}</FormLabel>
                    <FormControl>
                      <Input placeholder={t('services.registerProvider.countryPlaceholder')} {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>
        </Card>

        {/* Photos */}
        <Card className="p-4">
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold mb-2">{t('services.registerProvider.photos')} *</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {t('services.registerProvider.photosHelper')} (max 5)
              </p>
            </div>
            
            <div className="flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-6 bg-muted/30">
              <Upload className="w-8 h-8 mb-2 text-muted-foreground" />
              <label htmlFor="photo-upload" className="cursor-pointer">
                <Button 
                  type="button" 
                  variant="outline"
                  disabled={photos.length >= 5}
                  onClick={() => document.getElementById('photo-upload')?.click()}
                >
                  {photos.length === 0 
                    ? t('services.registerProvider.uploadPhotos')
                    : t('services.registerProvider.addMorePhotos')
                  }
                </Button>
              </label>
              <input
                id="photo-upload"
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoUpload}
                disabled={photos.length >= 5}
                className="hidden"
              />
              <p className="text-xs text-muted-foreground mt-2">
                {photos.length} / 5 {t('services.registerProvider.photosUploaded')}
              </p>
            </div>

            {photos.length > 0 && (
              <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                {photos.map((photo, idx) => (
                  <div key={idx} className="relative group">
                    <img
                      src={URL.createObjectURL(photo)}
                      alt={`Preview ${idx + 1}`}
                      className="w-full h-24 object-cover rounded-lg border-2 border-border"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute -top-2 -right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => setPhotos(photos.filter((_, i) => i !== idx))}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>

        {/* Services */}
        <Card className="p-4">
          <h3 className="font-semibold mb-4">{t('services.registerProvider.servicesOffered')}</h3>
          <div className="flex gap-2 mb-4">
            <Input
              placeholder={t('services.registerProvider.addServicePlaceholder')}
              value={newService}
              onChange={(e) => {
                const value = e.target.value;
                
                // Detectar si hay una coma para auto-agregar servicios
                if (value.includes(',')) {
                  const parts = value.split(',');
                  const completedServices = parts.slice(0, -1);
                  const currentInput = parts[parts.length - 1];
                  
                  // Agregar cada servicio completado
                  completedServices.forEach(service => {
                    const trimmedService = service.trim();
                    if (trimmedService && !services.includes(trimmedService)) {
                      setServices(prev => [...prev, trimmedService]);
                    }
                  });
                  
                  // Mantener solo el texto después de la última coma
                  setNewService(currentInput.trim());
                } else {
                  setNewService(value);
                }
              }}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddService())}
            />
            <Button type="button" onClick={handleAddService}>{t('services.registerProvider.addButton')}</Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {services.map((service) => (
              <Badge key={service} variant="secondary">
                {service}
                <X
                  className="w-3 h-3 ml-2 cursor-pointer"
                  onClick={() => handleRemoveService(service)}
                />
              </Badge>
            ))}
          </div>
        </Card>

        {/* Languages */}
        <Card className="p-4">
          <h3 className="font-semibold mb-4">{t('services.registerProvider.languagesSpoken')}</h3>
          <div className="flex gap-2 mb-4">
            <Input
              placeholder={t('services.registerProvider.addLanguagePlaceholder')}
              value={newLanguage}
              onChange={(e) => {
                const value = e.target.value;
                
                // Detectar si hay una coma para auto-agregar idiomas
                if (value.includes(',')) {
                  const parts = value.split(',');
                  const completedLanguages = parts.slice(0, -1);
                  const currentInput = parts[parts.length - 1];
                  
                  // Agregar cada idioma completado
                  completedLanguages.forEach(lang => {
                    const trimmedLang = lang.trim();
                    if (trimmedLang && !languages.includes(trimmedLang)) {
                      setLanguages(prev => [...prev, trimmedLang]);
                    }
                  });
                  
                  // Mantener solo el texto después de la última coma
                  setNewLanguage(currentInput.trim());
                } else {
                  setNewLanguage(value);
                }
              }}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddLanguage())}
            />
            <Button type="button" onClick={handleAddLanguage}>{t('services.registerProvider.addButton')}</Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {languages.map((language) => (
              <Badge key={language} variant="secondary">
                {language}
                <X
                  className="w-3 h-3 ml-2 cursor-pointer"
                  onClick={() => handleRemoveLanguage(language)}
                />
              </Badge>
            ))}
          </div>
        </Card>

        {/* Schedule - REQUIRED */}
        <ScheduleInput schedule={schedule} onChange={setSchedule} />

        {/* Professional Credentials - Required only for medical providers */}
        {(() => {
          const providerType = form.watch('provider_type');
          const isMedicalProvider = MEDICAL_PROVIDER_TYPES.includes(providerType as typeof MEDICAL_PROVIDER_TYPES[number]);
          const isOptional = !isMedicalProvider;
          const requiredMark = isMedicalProvider ? ' *' : '';
          
          return (
            <Card className="p-4">
              <h3 className="font-semibold mb-2">
                {t('services.registerProvider.credentials')}{requiredMark}
              </h3>
              {isOptional && (
                <p className="text-sm text-muted-foreground mb-4">
                  {t('services.registerProvider.credentialsOptionalNote')}
                </p>
              )}
          
              {/* ID Type and Number */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <FormField
                  control={form.control}
                  name="id_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('services.registerProvider.idType')}{requiredMark}</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={t('services.registerProvider.selectIdType')} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="bg-popover z-50">
                          <SelectItem value="national_id">
                            {t('services.registerProvider.idTypes.nationalId')}
                          </SelectItem>
                          <SelectItem value="passport">
                            {t('services.registerProvider.idTypes.passport')}
                          </SelectItem>
                          <SelectItem value="drivers_license">
                            {t('services.registerProvider.idTypes.driversLicense')}
                          </SelectItem>
                          <SelectItem value="residence_permit">
                            {t('services.registerProvider.idTypes.residencePermit')}
                          </SelectItem>
                          <SelectItem value="tax_id">
                            {t('services.registerProvider.idTypes.taxId')}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="id_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('services.registerProvider.idNumber')}{requiredMark}</FormLabel>
                      <FormControl>
                        <Input placeholder={t('services.registerProvider.idNumberPlaceholder')} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Professional Registration Number */}
              <FormField
                control={form.control}
                name="registration_number"
                render={({ field }) => (
                  <FormItem className="mb-4">
                    <FormLabel>{t('services.registerProvider.registrationNumber')}{requiredMark}</FormLabel>
                    <FormControl>
                      <Input placeholder={t('services.registerProvider.registrationNumberPlaceholder')} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Professional Credential Document */}
              <div className="mb-4">
                <FormLabel>{t('services.registerProvider.credentialDocument')}{requiredMark}</FormLabel>
                <TranslatedFileInput
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(file) => setCredential(file)}
                  value={credential}
                />
              </div>

              {/* ID Document Front */}
              <div className="mb-4">
                <FormLabel>
                  {watchedIdType === 'passport' 
                    ? t('services.registerProvider.idDocumentFrontPassport')
                    : t('services.registerProvider.idDocumentFrontOther')}{requiredMark}
                </FormLabel>
                <TranslatedFileInput
                  accept=".jpg,.jpeg,.png,.pdf"
                  onChange={(file) => setIdDocumentFront(file)}
                  value={idDocumentFront}
                />
                {watchedIdType === 'passport' && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('services.registerProvider.idDocumentFrontHelper')}
                  </p>
                )}
              </div>

              {/* ID Document Back - Only show if NOT passport */}
              {watchedIdType !== 'passport' && (
                <div className="mb-4">
                  <FormLabel>{t('services.registerProvider.idDocumentBack')}{requiredMark}</FormLabel>
                  <TranslatedFileInput
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={(file) => setIdDocumentBack(file)}
                    value={idDocumentBack}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('services.registerProvider.idDocumentBackHelper')}
                  </p>
                </div>
              )}
            </Card>
          );
        })()}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 pt-4">
          {/* Save Draft Button */}
          <Button 
            type="button"
            variant="outline"
            size="lg" 
            className="w-full sm:w-auto"
            onClick={handleSaveDraft}
            disabled={isSavingDraft}
          >
            {isSavingDraft ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                {t('common.saving')}...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {t('services.registerProvider.saveInformation')}
              </>
            )}
          </Button>

          {/* Proceed to Payment Button */}
          <Button 
            type="submit" 
            size="lg" 
            className="w-full sm:flex-1"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                {t('common.processing')}...
              </>
            ) : (
              <>
                <CreditCard className="w-4 h-4 mr-2" />
                {t('services.providerSubscription.proceedToPayment')}
              </>
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}
